/*
 * lumiateFixture.js — 공개 랜딩 V4의 유일한 수치·카피 원천.
 *
 * 여기 있는 브랜드·캠페인·소재·금액·상태는 전부 지어낸 값이다. 실제 광고주·계정·집행
 * 성과와 대응하지 않는다. 렌더러(PublicLandingPage.jsx·LandingScenes.jsx)에는 수치
 * 리터럴을 두지 않고 이 파일만 보유한다 — 수치가 한 곳에 모여 있어야 "이 숫자는 어디서
 * 왔나"를 감사할 수 있고, 카피 작업 중에 성과 주장이 새로 생기지 않는다.
 * (tests/p0-sales-entry.test.mjs V4 절이 이 격리를 기계 단언한다.)
 *
 * 공개 브랜드 정체성은 BRAND 하나에서만 나온다. 브랜드 표기(라틴·한글)는 이 파일에서 각각
 * 정확히 1회, 바로 아래 BRAND 정의로만 등장하고 그 밖의 모든 표기는 템플릿으로 파생한다 —
 * 데모 화면에 브랜드를 여럿 두면 실고객 명단처럼 읽히고, 리터럴이 흩어지면 임의 브랜드가
 * 슬쩍 섞여도 가드가 못 잡는다. 그래서 수집 신호등의 축도 브랜드가 아니라 매체 × 일자다.
 *
 * 장면 구성은 실제 제품 기능에 대응한다:
 *   sources     → mediaRegistry 연결 매체 + GA4 + 수동 업로드 병합
 *   collection  → src/utils/collectionStatus.js 6상태 · monitoring/CollectionGrid
 *   performance → App 통합 KPI · 기여 기준 표기(mpx-design §2-6c) · 캠페인/세트/소재 이동
 *   creative    → src/utils/creativeTier.js S/A/B/C + 이상징후 플래그
 *   journey     → GA4Section 세션·전환·매출
 *   reporting   → ReportTab · 내보내기 · ShareLinkModal 만료 · 근거 표/DecisionBriefView
 *   monitoring  → MonitoringTab 수집 control plane vs anomalyRules 성과 감시
 *   access      → AuthContext 도메인 제한 · 광고주 공유 링크 범위
 *
 * 구현되지 않은 기능은 주장하지 않는다. 공유 링크·결정 브리프를 광고주가 몇 번 봤는지 세는
 * 구현은 없으므로 그런 원장 행도 두지 않는다(2026-09-05 독립 리뷰 BLOCK).
 */

export const BRAND = Object.freeze({
  latin: "LUMIATE",
  ko: "루미아떼",
  code: "LU", // 소재 코드 접두사 — 매체 콘솔 네이밍 관습을 흉내 낸 합성 값
});
export const BRAND_ALLOWLIST = Object.freeze([BRAND.latin, BRAND.ko]);

export const SYNTHETIC_NOTICE = `이 페이지의 모든 화면·수치는 가상 브랜드 ${BRAND.ko}(${BRAND.latin})로 만든 합성 데모입니다.`;

export const DEMO = {
  brand: BRAND.ko,
  latin: BRAND.latin,
  period: "2026년 8월 1일 – 8월 31일",
  basis: "META 7일 클릭 · GA4 마지막 클릭 · 원 단위 표기",
  collectedAt: "2026-09-01 09:10 KST 수집 · D-1 기준",
};

// ── hero — display는 <br>가 아니라 행 배열이다(반응형 span으로 조판) ──
export const HERO = {
  eyebrow: "소스 → 검증 → 결정",
  lines: ["수집을 먼저", "증명하고 나서", "성과를 말합니다"],
  lede: "광고대행사 AE와 마케팅 리더가 쓰는 운영 화면입니다. 여덟 매체와 GA4를 한 브랜드 화면에 모으고, 어제 데이터가 다 들어왔는지부터 밝힌 뒤 성과 판단과 광고주 보고로 이어집니다.",
  artwork: {
    src: "/landing/data-convergence-hero.webp",
    width: 1600,
    height: 900,
  },
  /*
   * artwork 위에 얹는 조용한 callout. 추상 이미지를 장식으로 남겨두면 어느 SaaS에나 붙는
   * split hero가 된다 — 그래서 같은 자리에 이 데모가 실제로 무엇을 들고 있는지를 3단으로
   * 적는다. 값은 아래 장면 fixture와 같은 8월 31일 수집본이고, 구현된 상태만 쓴다.
   */
  callout: {
    tag: "합성 데모",
    steps: [
      { label: "소스", value: "8월 31일 · 매체 7종 수집" },
      { label: "근거", value: "TikTok 실패 1건 · NAVER SA 지연" },
      { label: "결정", value: "예산 유지 여부 · 공유 링크 발급" },
    ],
  },
  rail: [
    { label: "연결 범위", value: "매체 8종 · GA4 · 수동 업로드 병합" },
    { label: "수집 상태", value: "정상 · 지연 · 확인 · 실패 · 무집행 · 미연동" },
    { label: "성과 판단", value: "합계 → 캠페인 → 세트 → 소재" },
    { label: "보고 산출물", value: "리포트 · PPT · 광고주 공유 링크" },
  ],
};

// 연결 범위 — 매체 id는 CSS 매체색 클래스 키(고정 식별자)와 1:1.
export const SOURCES = [
  { id: "meta", label: "META Ads", detail: "캠페인 · 세트 · 소재" },
  { id: "google", label: "Google Ads", detail: "디스플레이 캠페인" },
  { id: "google-sa", label: "Google SA", detail: "검색 광고그룹" },
  { id: "naver", label: "NAVER SA", detail: "파워링크 · 브랜드검색" },
  { id: "naver-gfa", label: "NAVER GFA", detail: "소재 단위" },
  { id: "kakao", label: "KAKAO Moment", detail: "광고그룹" },
  { id: "tiktok", label: "TikTok Ads", detail: "영상 지표 포함" },
  { id: "criteo", label: "Criteo", detail: "리타게팅" },
  { id: "ga4", label: "GA4", detail: "세션 · 전환 · 매출" },
  { id: "manual", label: "수동 매체", detail: "엑셀 업로드 병합" },
];

// 수집 상태 6종 — collectionStatus.js deriveCellState 반환값과 같은 어휘.
// 축은 매체 × 일자다(브랜드 축 아님 — 공개면 브랜드는 하나뿐).
export const COLLECTION = {
  brand: BRAND.ko,
  days: ["8월 29일", "8월 30일", "8월 31일"],
  shortDays: ["29일", "30일", "31일"],
  rows: [
    { media: "META", cells: ["ok", "ok", "ok"] },
    { media: "Google Ads", cells: ["ok", "ok", "ok"] },
    { media: "NAVER SA", cells: ["delay_warn", "delay_alert", "delay_alert"] },
    { media: "NAVER GFA", cells: ["ok", "ok", "ok"] },
    { media: "KAKAO", cells: ["dormant", "dormant", "dormant"] },
    { media: "TikTok", cells: ["ok", "fail", "ok"] },
    { media: "Criteo", cells: ["unlinked", "unlinked", "unlinked"] },
  ],
  legend: [
    { state: "ok", label: "정상", short: "정상" },
    { state: "delay_warn", label: "수집 지연", short: "지연" },
    { state: "delay_alert", label: "확인 필요", short: "확인" },
    { state: "fail", label: "수집 실패", short: "실패" },
    { state: "dormant", label: "무집행", short: "무집행" },
    { state: "unlinked", label: "미연동", short: "미연동" },
  ],
  actions: [
    "확인 필요 — NAVER SA가 사흘째 밀렸습니다. 기간을 지정해 재수집을 겁니다.",
    "수집 실패 — TikTok 8월 30일, 인증이 만료됐습니다. 키를 갱신하고 그 날짜만 다시 받습니다.",
  ],
};

// 통합 성과 — 금액은 원 단위 전체 표기(축약 금지), 델타는 좋음/나쁨 기준.
export const PERFORMANCE = {
  metrics: [
    { label: "광고비", value: "₩48,720,000", delta: "+12.4%", tone: "flat" },
    { label: "노출", value: "6,184,290", delta: "+8.1%", tone: "flat" },
    { label: "클릭", value: "92,418", delta: "+15.7%", tone: "up" },
    { label: "전환", value: "1,236", delta: "+21.2%", tone: "up" },
    { label: "매출", value: "₩214,860,000", delta: "+27.9%", tone: "up" },
    { label: "ROAS", value: "4.41배", delta: "+0.52배", tone: "up" },
  ],
  compare: "직전 31일(7월) 대비",
};

// 캠페인 → 세트 → 소재 드릴다운. 캠페인명은 BRAND에서 파생한다(리터럴 금지).
export const DRILLDOWN = {
  path: ["매체 합계", "캠페인", "광고 세트", "소재"],
  columns: ["캠페인", "광고비", "전환", "ROAS"],
  rows: [
    {
      name: `${BRAND.latin}_2608_리타겟_전환`,
      media: "meta",
      spend: "₩21,340,000",
      cv: "684",
      roas: "5.82배",
      tone: "up",
    },
    {
      name: `${BRAND.latin}_2608_신규_트래픽`,
      media: "google",
      spend: "₩14,920,000",
      cv: "312",
      roas: "3.14배",
      tone: "flat",
    },
    {
      name: `${BRAND.latin}_2608_브랜드검색`,
      media: "naver",
      spend: "₩7,180,000",
      cv: "196",
      roas: "6.05배",
      tone: "up",
    },
  ],
  drilled: {
    campaign: `${BRAND.latin}_2608_리타겟_전환`,
    adset: "리타겟_30일_여성2534",
    creative: `${BRAND.code}_0812_리필세트_A`,
    line: "세트 6개 중 2개가 전환의 71%를 냅니다.",
  },
};

/*
 * 소재 티어 — creativeTier.js S/A/B/C + 이상징후 플래그.
 *
 * 이미지는 이 데모를 위해 만든 합성 광고 소재다(실제 브랜드 촬영물 아님). 등급·수치를
 * 이미지 옆에 붙여야 "소재를 등급으로 나눈다"는 주장이 그림으로 증명된다 — 점선 자리표시자는
 * 와이어프레임처럼 읽혀 판매 페이지의 근거가 되지 못한다(2026-09-05 독립 리뷰 BLOCK).
 * 원본이 정사각이라 1:1 컨테이너에서 object-fit:cover 가 제품·병·리본을 자르지 않는다.
 */
export const CREATIVE_IMAGE = { width: 768, height: 768 };

export const CREATIVES = [
  {
    code: `${BRAND.code}_0812_리필세트_A`,
    tier: "S",
    image: "/landing/creative-refill.webp",
    alt: "합성 데모 소재 — 햇빛 든 석재 선반 위 리필 파우치와 흰 펌프 보틀",
    spend: "₩8,420,000",
    roas: "7.36배",
    flag: null,
    note: "8월 12일 교체 후 상위 유지",
  },
  {
    code: `${BRAND.code}_0731_앰플_전성분_B`,
    tier: "A",
    image: "/landing/creative-ampoule.webp",
    alt: "합성 데모 소재 — 물결 위에 세운 은색 캡의 파란 앰플 보틀",
    spend: "₩5,110,000",
    roas: "4.02배",
    flag: null,
    note: "클릭률은 높고 전환은 평이",
  },
  {
    code: `${BRAND.code}_0705_기획전_카드뉴스`,
    tier: "C",
    image: "/landing/creative-gift.webp",
    alt: "합성 데모 소재 — 어두운 석재 배경에 남색 리본을 두른 기획 세트 박스와 용기 세 점",
    spend: "₩6,980,000",
    roas: "0.94배",
    flag: "고지출 · 낮은 ROAS",
    note: "노출 피로 누적, 교체 후보",
  },
  {
    code: `${BRAND.code}_0820_리뷰_숏폼`,
    tier: "B",
    image: "/landing/creative-shortform.webp",
    alt: "합성 데모 소재 — 남색 리본 위 프로스티드 보틀에 스포이드로 세럼을 떨어뜨리는 장면",
    spend: "₩2,240,000",
    roas: "3.11배",
    flag: "판정 제외",
    note: "볼륨 미달 — 데이터가 더 필요합니다",
  },
];

export const CREATIVE_BASIS =
  "소재 이미지는 이 데모를 위해 만든 합성 이미지입니다. 실제 화면에서는 매체에서 받아온 소재가 같은 자리에 들어갑니다.";

// GA4 유입·행동·전환 — 값은 세션 기준, ratio는 막대 길이(0–100).
export const JOURNEY = {
  steps: [
    { label: "세션", value: "128,412", ratio: 100 },
    { label: "참여 세션", value: "74,106", ratio: 58 },
    { label: "장바구니", value: "9,264", ratio: 21 },
    { label: "구매", value: "1,236", ratio: 9 },
  ],
  sources: [
    { label: "meta / cpc", value: "41,208" },
    { label: "google / cpc", value: "29,640" },
    { label: "naver / cpc", value: "18,315" },
    { label: "(organic)", value: "22,970" },
  ],
  state: {
    label: "부분 수집",
    detail: "구매 이벤트는 연동, 장바구니 이벤트는 8월 14일부터만 들어옵니다.",
  },
};

/*
 * 보고·근거 원장 — 리포트 → 문서 → 공유 링크 → 근거 표.
 * 마지막 행은 "광고주가 몇 번 봤는지"가 아니라 "무엇이 같은 수집본에서 나왔는지"다.
 * 조회 추적 기능은 없다 — 합성 숫자로 미구현 기능을 증명처럼 보이게 하지 않는다.
 */
export const REPORTING = {
  rows: [
    { step: "월간 리포트 생성", actor: "AE", at: "09-01 10:24", state: "완료", tone: "ok" },
    { step: "PPT 내보내기", actor: "AE", at: "09-01 10:31", state: "24장", tone: "ok" },
    {
      step: "광고주 공유 링크 발급",
      actor: "AE",
      at: "09-01 10:33",
      state: "9월 8일 만료",
      tone: "warn",
    },
    {
      step: "결정 브리프 · 근거 표",
      actor: "AE",
      at: "09-01 10:36",
      state: "같은 수집본",
      tone: "ok",
    },
  ],
  basis: "보고서의 모든 수치는 같은 수집본에서 나옵니다. 근거 표를 함께 엽니다.",
};

// 운영 감시 — 수집 control plane과 성과 이상치는 다른 판이다. + 하지 않는 것.
export const OPERATIONS = {
  columns: [
    {
      kind: "수집",
      title: "수집이 도는지 보는 판",
      items: [
        { label: "오늘 수집", value: "13개 계정 성공 · 1개 실패" },
        { label: "재수집", value: "브랜드 · 매체 · 기간 지정 실행" },
        { label: "수집 로그", value: "실행 시각 · 적재 행 수 · 오류" },
      ],
    },
    {
      kind: "성과",
      title: "숫자가 튀는지 보는 판",
      items: [
        { label: "확인 필요", value: "CPA 급등 2건" },
        { label: "주의", value: "ROAS 급락 1건" },
        { label: "판정 제외", value: "3행 — 소액 · 볼륨 미달" },
      ],
    },
  ],
  note: "이상치 임계는 브랜드별로 조정하고, 판정에서 빠진 행은 이유와 함께 남깁니다.",
  limits: [
    "매체 API가 주지 않는 값은 만들어 넣지 않습니다.",
    "상위 합계를 하위 행으로 나눠 배분하지 않습니다.",
    "수집이 덜 된 기간을 정상 성과처럼 표시하지 않습니다.",
    "예측치나 보장 수치를 화면에 올리지 않습니다.",
  ],
};

// 역할·접근 — 누가 무엇까지 보는지.
export const ACCESS = [
  {
    who: "OPINNO 운영자",
    what: "발급된 opinno.co.kr 계정으로 로그인합니다. 담당 브랜드만 열립니다.",
  },
  {
    who: "광고주",
    what: "담당자가 전달한 전용 공유 링크로 들어옵니다. 링크에는 만료가 있고, 보이는 범위는 담당자가 정합니다.",
  },
  { who: "관리자", what: "브랜드 등록과 매체 연결, 수집 재실행을 맡습니다." },
];

const CLOSE_TITLE_LINES = ["지금의 운영 방식부터", "확인하겠습니다."];

export const CLOSE = {
  titleLines: CLOSE_TITLE_LINES,
  title: CLOSE_TITLE_LINES.join(" "),
  body: "쓰고 있는 매체 구성과 보고 흐름을 먼저 보고, MEDIAPLEX가 어디까지 맡을 수 있는지 정리해 드립니다.",
};

/*
 * 실제 QA 캡처 2종 — 데스크톱은 원본 배율에 가깝게 두고, 모바일은 전체 축소본 대신
 * crop(원본 픽셀 1:1로 잘라낸 창)과 native 텍스트 callout으로 읽힌다. crop 좌표는
 * 캡처 안에서 "이 장면이 증명하려는 영역"의 좌상단 오프셋과 창 높이이고, 2026-09-05에
 * 실제 캡처를 열어 보며 확정했다 — 소스 행 4개(collection)와 전환·ROAS 열(output)이
 * 가로로 잘리지 않는 자리다. 좌표는 회귀 가드가 핀으로 잡고 있다.
 *
 * 아래 rows 수치는 캡처에 실제로 찍혀 있는 값(90일 기준)이며 위 장면 fixture(8월 31일
 * 기준)와는 기간이 다르다 — 서로 다른 화면이라 일부러 섞지 않고, period 라벨로 화면에
 * 명시한다.
 */
const PERIOD_SWITCH = "위 장면과 다른 기간입니다 — 2026-08-26 수집 · 90일 기준";

export const PROOF = {
  collection: {
    capture: "operations-qa.webp",
    src: "/landing/operations-qa.webp",
    width: 1280,
    height: 1049,
    alt: "합성 데모 데이터로 구성된 MEDIAPLEX 수집 상태와 성과 근거 화면",
    caption: "소스별 마지막 성공 시각과 재집계 lookback을 한 줄씩 대조합니다.",
    crop: { x: 104, y: 264, h: 154 },
    cropLead: "소스 신선도 행을 원본 배율로 확대",
    period: PERIOD_SWITCH,
    lead: "캡처 화면이 말하는 것",
    rows: [
      { label: "수집 시각", value: "2026-08-26 06:10 KST · 90일 기간" },
      {
        label: "소스 신선도",
        value: "META · GOOGLE · NAVER SA · KAKAO · TikTok 정상",
        state: "ok",
      },
      {
        label: "범위 제외",
        value: "Criteo 0행 — 시연 계약상 제외(실패 아님)",
        state: "dormant",
      },
      {
        label: "예외 큐",
        value: "4건 — ROAS · 키워드 · 썸네일 · CPA",
        state: "delay_warn",
      },
      { label: "근거 패널", value: "ROAS = 매출 ÷ 광고비 × 100 · META 7d click" },
    ],
  },
  output: {
    capture: "decision-brief-qa.webp",
    src: "/landing/decision-brief-qa.webp",
    width: 1280,
    height: 1067,
    alt: `가상 시연 브랜드 ${BRAND.ko}의 MEDIAPLEX 광고주 결정 브리프 화면`,
    caption: "요약 아래에 결정 요청과 상세 근거가 같은 수집본으로 이어집니다.",
    crop: { x: 646, y: 484, h: 82 },
    cropLead: "전환·ROAS 열을 원본 배율로 확대",
    period: PERIOD_SWITCH,
    lead: "캡처 화면이 말하는 것",
    rows: [
      { label: "광고비", value: "₩2,563,790,751 (+21.0%)" },
      { label: "매출", value: "₩9,411,238,600 (+30.6%)", state: "ok" },
      { label: "전환", value: "34,182건 (+18.3%)", state: "ok" },
      { label: "ROAS", value: "367.1% — 직전 340.1% · +27.0%p", state: "ok" },
      { label: "CPA", value: "₩75,004 — +₩1,712 (+2.3%)", state: "delay_warn" },
      {
        label: "결정 요청",
        value: "사흘 연속 목표 미달한 KAKAO 리타게팅 예산 유지 여부",
        state: "delay_alert",
      },
    ],
  },
};

const note = (s) => `${s} · 합성 데모 데이터`;

/*
 * 장면 제목은 문자열이 아니라 행 그룹이다. 자동 줄바꿈에 맡기면 좁은 head 열과 모바일에서
 * "봅니다 / 나옵니다 / 답니다"가 혼자 남는 고아 행이 생긴다(2026-09-05 독립 리뷰 BLOCK).
 * 각 행은 조판 예산 10em 안이고, 최단행/최장행 비율은 0.5 이상이다 — 가드가 단언한다.
 */
const scene = ({ titleLines, ...rest }) => ({
  ...rest,
  titleLines,
  title: titleLines.join(" "),
});

export const SCENES = [
  scene({
    id: "sources",
    composition: "axis",
    anchor: "features",
    eyebrow: "01 — 연결 범위",
    titleLines: ["흩어진 매체를", "한 브랜드 화면으로"],
    body: "매체별 콘솔을 오가지 않습니다. 계정을 연결하면 캠페인·세트·소재 단위까지 같은 기간, 같은 통화로 정렬됩니다. 엑셀로만 오는 매체는 업로드해서 같은 표에 붙입니다.",
    note: note("연결 범위"),
  }),
  scene({
    id: "collection",
    composition: "matrix",
    eyebrow: "02 — 수집 신뢰",
    titleLines: ["숫자를 읽기 전에,", "다 들어왔는지 봅니다"],
    body: "매체 × 일자로 어제 데이터가 들어왔는지 먼저 표시합니다. 지연·실패·미연동을 정상 성과처럼 섞지 않고, 무엇을 다시 돌려야 하는지까지 적습니다.",
    note: note("수집 상태"),
  }),
  scene({
    id: "performance",
    composition: "sticky",
    eyebrow: "03 — 성과 근거",
    titleLines: ["합계에서 멈추지 않고", "달라진 지점까지"],
    body: "광고비·노출·클릭·전환·매출을 같은 기간에서 비교하고, 어떤 기여 기준으로 센 숫자인지 함께 붙입니다. 캠페인에서 세트로, 세트에서 소재로 내려가며 어디서 숫자가 갈렸는지 봅니다.",
    note: note("통합 성과 · 드릴다운"),
  }),
  scene({
    id: "creative",
    composition: "mosaic",
    eyebrow: "04 — 소재",
    titleLines: ["소재는 등급과", "이유를 함께 답니다"],
    body: "지출·전환·ROAS로 소재를 S·A·B·C로 나누고, 고지출·낮은 ROAS 같은 이상징후를 표시합니다. 볼륨이 모자란 소재는 좋다 나쁘다 하지 않고 판정에서 빼둡니다.",
    note: note("소재 분석"),
  }),
  scene({
    id: "journey",
    composition: "map",
    eyebrow: "05 — GA4 여정",
    titleLines: ["광고 밖에서 벌어진", "일도 봅니다"],
    body: "GA4 세션·참여·구매를 광고 성과 옆에 둡니다. 다만 연동 상태를 숨기지 않습니다. 이벤트가 일부만 들어오는 기간은 그 사실을 먼저 적습니다.",
    note: note("GA4 유입 · 전환"),
  }),
  scene({
    id: "reporting",
    composition: "output",
    anchor: "flow",
    eyebrow: "06 — 보고 산출물",
    titleLines: ["보고서와 근거가 같은", "수집본에서 나옵니다"],
    body: "리포트에서 문서로, 문서에서 광고주 공유 링크로 이어집니다. 광고주는 전달받은 범위만 보고, 링크에는 만료가 붙습니다. 숫자가 이상하면 근거 표를 그 자리에서 엽니다.",
    note: note("보고 · 공유 근거"),
  }),
  scene({
    id: "monitoring",
    composition: "ledger",
    eyebrow: "07 — 감시와 한계",
    titleLines: ["수집 감시와", "성과 감시를", "섞지 않습니다"],
    body: "수집이 안 돈 것과 성과가 튄 것은 다른 문제입니다. 판을 나눠서 봅니다. 한쪽은 다시 돌리면 되고, 다른 쪽은 판단이 필요합니다. 대신 하지 않는 것도 분명히 적어 둡니다.",
    note: note("운영 감시 · 한계"),
  }),
  scene({
    id: "access",
    composition: "close",
    eyebrow: "08 — 역할과 시작",
    titleLines: ["누가 무엇까지 보는지", "먼저 정합니다"],
    body: "운영자는 발급된 계정으로, 광고주는 만료가 있는 전용 링크로 들어옵니다. 브랜드 등록과 매체 연결, 수집 재실행은 관리자가 맡습니다. 권한을 정하고 나서 화면을 엽니다.",
    note: note("역할 · 접근"),
  }),
];
